import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, ChevronRight, ChevronLeft, Mail, Settings, Loader2, ExternalLink } from 'lucide-react';
import { companyApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * SMTP setup wizard with provider presets (Gmail, OVH, PlanetHoster, Outlook, Custom).
 * 3 steps : provider choice → credentials → test
 *
 * Props:
 *  - open, onOpenChange
 *  - currentFormData, onApply(values)  // values match formData smtp_* keys
 */
const PRESETS = [
  {
    id: 'planethoster',
    name: 'PlanetHoster',
    logo: '🌐',
    host: 'mail.planethoster.com',
    port: 587,
    notes: 'Utilisez l\'email + le mot de passe de votre compte de messagerie chez PlanetHoster.',
    helpUrl: 'https://hosting.planethoster.com/configurer-vos-comptes-courriel/',
  },
  {
    id: 'ovh',
    name: 'OVH',
    logo: '🅾️',
    host: 'ssl0.ovh.net',
    port: 587,
    notes: 'Configuration MX Plan / Email Pro OVH. Mot de passe = mot de passe du compte mail (pas du compte OVH).',
    helpUrl: 'https://help.ovhcloud.com/csm/fr-mx-plan-imap-pop-config?id=kb_article_view&sysparm_article=KB0051737',
  },
  {
    id: 'gmail',
    name: 'Gmail / Google Workspace',
    logo: '📧',
    host: 'smtp.gmail.com',
    port: 587,
    notes: 'IMPORTANT : générez un "Mot de passe d\'application" (pas votre mot de passe Gmail). 2FA requis.',
    helpUrl: 'https://myaccount.google.com/apppasswords',
  },
  {
    id: 'outlook',
    name: 'Outlook / Office 365',
    logo: '📨',
    host: 'smtp.office365.com',
    port: 587,
    notes: 'Microsoft impose souvent l\'authentification moderne — préférez un compte SMTP dédié.',
    helpUrl: 'https://support.microsoft.com/fr-fr/office/parametres-pop-imap-et-smtp-pour-outlook-com-d088b986-291d-42b8-9564-9c414e2aa040',
  },
  {
    id: 'custom',
    name: 'Autre fournisseur',
    logo: '⚙️',
    host: '',
    port: 587,
    notes: 'Saisissez manuellement les informations fournies par votre hébergeur email.',
    helpUrl: null,
  },
];

export default function SmtpWizard({ open, onOpenChange, currentFormData = {}, onApply, onTestComplete }) {
  const [step, setStep] = useState(0);
  const [provider, setProvider] = useState(null);
  const [creds, setCreds] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: currentFormData.email || '',
    smtp_password: '',
    smtp_sender_email: currentFormData.email || '',
    smtp_sender_name: currentFormData.name || '',
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const reset = () => {
    setStep(0);
    setProvider(null);
    setTestResult(null);
    setTesting(false);
  };

  const handleSelectPreset = (preset) => {
    setProvider(preset);
    setCreds((c) => ({
      ...c,
      smtp_host: preset.host || c.smtp_host,
      smtp_port: preset.port || c.smtp_port,
      smtp_user: c.smtp_user || currentFormData.email || '',
      smtp_sender_email: c.smtp_sender_email || currentFormData.email || '',
      smtp_sender_name: c.smtp_sender_name || currentFormData.name || '',
    }));
    setStep(1);
  };

  const handleApplyAndTest = async () => {
    // First apply the values to parent form so save persists
    if (onApply) {
      onApply({
        smtp_enabled: true,
        smtp_host: creds.smtp_host,
        smtp_port: creds.smtp_port,
        smtp_user: creds.smtp_user,
        smtp_password: creds.smtp_password,
        smtp_sender_email: creds.smtp_sender_email,
        smtp_sender_name: creds.smtp_sender_name,
      });
    }
    // Persist (call company save) — handled by parent's onApply effect or save button
    setTesting(true);
    setTestResult(null);
    try {
      // We need parent to save first OR have an endpoint that tests arbitrary creds.
      // Simplest: save then test connection on the saved config.
      if (onTestComplete) await onTestComplete();
      const res = await companyApi.testSmtpConnection();
      setTestResult(res.data);
      if (res.data?.success) {
        toast.success('Connexion SMTP réussie !');
      } else {
        toast.error(`Échec : ${res.data?.error || 'Erreur inconnue'}`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erreur de test';
      setTestResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
      setStep(2);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="smtp-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Settings className="w-5 h-5" /> Assistant configuration SMTP
          </DialogTitle>
          <DialogDescription>
            Configurez l'envoi d'emails depuis votre propre adresse en 3 étapes.
          </DialogDescription>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 text-xs">
          {['Fournisseur', 'Identifiants', 'Test'].map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium ${i === step ? 'bg-slate-900 text-white' : i < step ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={i === step ? 'font-medium text-slate-800' : 'text-slate-500'}>{label}</span>
              {i < 2 && <ChevronRight className="w-3 h-3 text-slate-300" />}
            </div>
          ))}
        </div>

        {/* Step 0 — provider */}
        {step === 0 && (
          <div className="space-y-2 mt-4">
            <p className="text-sm text-slate-600">Quel est votre fournisseur d'email ?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelectPreset(p)}
                  className="text-left p-3 rounded-xl border hover:border-slate-900 hover:bg-slate-50 transition-colors"
                  data-testid={`smtp-wizard-preset-${p.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{p.logo}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-900">{p.name}</p>
                      {p.host && <p className="text-xs text-slate-500 truncate">{p.host} · port {p.port}</p>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1 — credentials */}
        {step === 1 && provider && (
          <div className="space-y-3 mt-4">
            {provider.notes && (
              <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-xs text-sky-900">
                <p className="font-medium mb-0.5">{provider.name}</p>
                <p>{provider.notes}</p>
                {provider.helpUrl && (
                  <a href={provider.helpUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 underline hover:no-underline">
                    Documentation officielle <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Serveur SMTP</Label>
                <Input
                  value={creds.smtp_host}
                  onChange={(e) => setCreds({ ...creds, smtp_host: e.target.value })}
                  placeholder="mail.example.com"
                  data-testid="smtp-wizard-host"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Port</Label>
                <Input
                  type="number"
                  value={creds.smtp_port}
                  onChange={(e) => setCreds({ ...creds, smtp_port: Number(e.target.value) || 587 })}
                  data-testid="smtp-wizard-port"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Utilisateur (adresse email complète)</Label>
                <Input
                  value={creds.smtp_user}
                  onChange={(e) => setCreds({ ...creds, smtp_user: e.target.value })}
                  placeholder="contact@votredomaine.fr"
                  data-testid="smtp-wizard-user"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Mot de passe</Label>
                <Input
                  type="password"
                  value={creds.smtp_password}
                  onChange={(e) => setCreds({ ...creds, smtp_password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="smtp-wizard-password"
                />
                <p className="text-[11px] text-slate-500">Pour Gmail/Outlook, utilisez un mot de passe d'application.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email expéditeur</Label>
                <Input
                  value={creds.smtp_sender_email}
                  onChange={(e) => setCreds({ ...creds, smtp_sender_email: e.target.value })}
                  placeholder="noreply@votredomaine.fr"
                  data-testid="smtp-wizard-sender-email"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nom expéditeur</Label>
                <Input
                  value={creds.smtp_sender_name}
                  onChange={(e) => setCreds({ ...creds, smtp_sender_name: e.target.value })}
                  placeholder="Mon Entreprise"
                  data-testid="smtp-wizard-sender-name"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — test result */}
        {step === 2 && (
          <div className="space-y-3 mt-4 text-center py-4" data-testid="smtp-wizard-result">
            {testing ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-slate-400 animate-spin" />
                <p className="text-sm text-slate-600">Test de la connexion en cours...</p>
              </div>
            ) : testResult?.success ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-emerald-700" />
                </div>
                <p className="text-base font-semibold text-emerald-900">Connexion SMTP opérationnelle !</p>
                <p className="text-sm text-slate-600">
                  Vos emails partiront désormais depuis <span className="font-medium">{creds.smtp_sender_name} &lt;{creds.smtp_sender_email}&gt;</span>.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-rose-700" />
                </div>
                <p className="text-base font-semibold text-rose-900">Échec du test</p>
                <p className="text-sm text-rose-700 max-w-md whitespace-pre-wrap">{testResult?.error || 'Erreur inconnue'}</p>
                <p className="text-xs text-slate-500 mt-1">Revenez à l'étape précédente pour corriger les identifiants.</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 0 && step < 2 && (
            <Button type="button" variant="outline" onClick={() => setStep(step - 1)} data-testid="smtp-wizard-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
            </Button>
          )}
          {step === 0 && (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          )}
          {step === 1 && (
            <Button
              type="button"
              onClick={handleApplyAndTest}
              disabled={!creds.smtp_host || !creds.smtp_user || !creds.smtp_password || !creds.smtp_sender_email || testing}
              data-testid="smtp-wizard-next"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Tester & enregistrer
            </Button>
          )}
          {step === 2 && (
            <>
              {testResult?.success ? (
                <Button type="button" onClick={() => { onOpenChange(false); reset(); }} data-testid="smtp-wizard-finish">
                  <Check className="w-4 h-4 mr-1" /> Terminer
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setStep(1)} data-testid="smtp-wizard-retry">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Modifier les identifiants
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
